/* NEXT50 BOGO pop-up — see sections/next50-bogo-popup.liquid */
(() => {
  const FOCUSABLE = 'a[href]:not([hidden]), button:not([disabled]):not([hidden]), [tabindex]:not([tabindex="-1"])';

  function makeStore(prefix, memoryOnly) {
    const memory = new Map();
    return {
      get(key) {
        if (memoryOnly) return memory.get(key) ?? null;
        try { return localStorage.getItem(prefix + key); } catch { return memory.get(key) ?? null; }
      },
      set(key, value) {
        memory.set(key, value);
        if (memoryOnly) return;
        try { localStorage.setItem(prefix + key, value); } catch { /* storage blocked */ }
      },
      clear() {
        memory.clear();
        if (memoryOnly) return;
        try {
          Object.keys(localStorage).filter((k) => k.startsWith(prefix)).forEach((k) => localStorage.removeItem(k));
        } catch { /* storage blocked */ }
      },
    };
  }

  const params = new URLSearchParams(window.location.search);
  const TEST_MODE = params.get('bogo_test') === '1' || params.get('bogo') === 'open';

  const visibleRect = (el) => {
    if (!el) return null;
    const style = getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return null;
    const rect = el.getBoundingClientRect();
    return rect.width && rect.height ? rect : null;
  };

  // Height of anything pinned to the bottom edge that the pill must clear (Shopify preview bar, sticky buy bar).
  function bottomObstruction() {
    const vh = window.innerHeight;
    let lift = 0;
    const bars = document.querySelectorAll('#PBarNextFrameWrapper, #preview-bar-iframe, [id*="preview-bar"], [id*="PreviewBar"], .n50-pdp-buy__sticky.is-visible');
    bars.forEach((el) => {
      const rect = visibleRect(el);
      if (rect && rect.bottom >= vh - 4 && rect.top < vh) lift = Math.max(lift, vh - rect.top);
    });
    if (!lift && window.Shopify?.theme?.role && window.Shopify.theme.role !== 'main' && !window.Shopify.designMode) {
      lift = 56; // Preview bar lives in a closed shadow root on some storefronts; assume its height.
    }
    return lift;
  }

  function format(ms) {
    const total = Math.ceil(ms / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = String(total % 60).padStart(2, '0');
    return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${String(m).padStart(2, '0')}:${s}`;
  }

  function isVisible(el) {
    return !el.closest('[hidden]') && el.getClientRects().length > 0;
  }

  function init(root, force = false) {
    if (root.dataset.ready) return;
    root.dataset.ready = '1';
    let testMode = TEST_MODE || force;

    const dialog = root.querySelector('[data-n50-bogo-dialog]');
    const pill = root.querySelector('[data-n50-bogo-pill]');
    const times = root.querySelectorAll('[data-n50-bogo-time]');
    const designMode = root.dataset.designMode === 'true';
    const durationMs = Math.max(1, Number(root.dataset.minutes) || 30) * 60000;
    const delay = Number(root.dataset.delay);
    // soft = keep the offer claimable after the countdown (static "still available" pill); message / hide = end it.
    const expiredMode = ['hide', 'soft'].includes(root.dataset.expired) ? root.dataset.expired : 'message';
    const store = makeStore(`n50:bogo:${root.dataset.campaign || 'bogo1'}:`, designMode);

    let returnFocus = null;
    let ticker = null;
    let expired = false;
    const cleanups = [];

    const startedAt = () => Number(store.get('start')) || 0;
    const remaining = () => {
      const start = startedAt();
      return start ? Math.max(0, start + durationMs - Date.now()) : durationMs;
    };

    const claimed = () => Boolean(store.get('claimed'));
    // Claimed or arriving from a cart link: never auto-open, but keep the pill while the timer runs.
    let pillOnly = false;

    if (testMode) {
      store.clear();
    } else if (!designMode) {
      if (startedAt() && remaining() === 0 && expiredMode !== 'soft') { root.dataset.state = 'expired'; return; }
      if (claimed()) {
        root.dataset.state = 'claimed';
        pillOnly = true;
      } else if (root.dataset.suppress === 'true' || params.get('open_cart') === '1') {
        root.dataset.state = 'suppressed';
        pillOnly = true;
      }
    }
    root.hidden = false;

    function setState(name) {
      root.querySelectorAll('[data-n50-bogo-state]').forEach((el) => {
        el.hidden = el.dataset.n50BogoState !== name;
      });
    }

    function stopTriggers() {
      while (cleanups.length) cleanups.pop()();
    }

    function expire() {
      expired = true;
      clearInterval(ticker);
      ticker = null;
      if (expiredMode === 'soft') {
        // Buttons, code and fine print stay; only the countdown pills change. The floating pill stays available.
        root.classList.add('n50-bogo--soft-expired');
        root.querySelectorAll('.n50-bogo__mtimer').forEach((el) => {
          el.hidden = !el.hasAttribute('data-n50-bogo-soft');
        });
        if (pill && !pill.hidden) showPill();
        return;
      }
      setState('expired');
      if (testMode) {
        // Keep a way back in while testing, even in "hide" mode.
        if (!dialog.open) showPill();
        return;
      }
      if (pill) { pill.hidden = true; syncPill(); }
      if (expiredMode === 'hide') {
        if (dialog.open) dialog.close();
        root.hidden = true;
      }
    }

    // Bottom-left, on the same baseline as the global "Top ↑" button (bottom-right). Both clear the preview / sticky
    // buy bars by bar height + 12px; the CSS adds the base inset and env(safe-area-inset-bottom).
    function placePill() {
      if (!pill || pill.hidden) return;
      // Follow "Top ↑"'s own lift whenever it has one so the two stay level; otherwise measure the bars ourselves.
      const bttRoot = document.querySelector('[data-n50-back-to-top]');
      const bttLift = bttRoot ? parseFloat(bttRoot.style.getPropertyValue('--n50-btt-extra-bottom')) || 0 : 0;
      const obstruction = bttLift ? 0 : bottomObstruction();
      const lift = bttLift || (obstruction ? obstruction + 12 : 0);
      pill.style.setProperty('--n50-bogo-pill-lift', `${Math.round(lift)}px`);
      // Width cap keeps a 12px gap to "Top ↑"; it stays laid out while hidden (visibility only), so its width is reliable.
      const backToTop = document.querySelector('[data-n50-back-to-top-scroll]');
      const reserve = backToTop ? Math.ceil(backToTop.getBoundingClientRect().width) + 12 : 0;
      pill.style.setProperty('--n50-bogo-pill-reserve', `${reserve}px`);
    }

    // `pill.hidden` = offer pill is available; `.is-shown` = actually on screen. It only shows once the visitor has
    // scrolled (not programmatic jumps) since the pop-up closed / the page loaded, and never while buy controls are
    // on screen, so it doesn't sit on top of the purchase UI.
    const SCROLL_ARM_PX = 40;
    const INTENT_MS = 800;
    const SCROLL_KEYS = new Set([' ', 'PageDown', 'PageUp', 'ArrowDown', 'ArrowUp', 'Home', 'End']);
    let userScrolled = false;
    let armY = 0;
    let lastIntent = 0;
    let controlsOnScreen = false;
    let revealTimer = null;

    function syncPill() {
      if (!pill) return;
      const show = !pill.hidden && userScrolled && !controlsOnScreen && !dialog.open;
      if (show === pill.classList.contains('is-shown')) return;
      pill.classList.toggle('is-shown', show);
      pill.setAttribute('aria-hidden', show ? 'false' : 'true');
      pill.inert = !show;
      pill.tabIndex = show ? 0 : -1;
    }

    function armScroll() {
      userScrolled = false;
      armY = window.scrollY;
      syncPill();
    }

    function showPill(rearm = false) {
      if (!pill) return;
      const mode = claimed() ? 'claimed' : expired && expiredMode === 'soft' ? 'soft' : 'open';
      pill.dataset.mode = mode;
      pill.querySelectorAll('[data-n50-bogo-pill-text]').forEach((el) => {
        el.hidden = el.dataset.n50BogoPillText !== mode;
      });
      pill.hidden = false;
      placePill();
      if (rearm) armScroll();
      else syncPill();
    }

    function watchPillGates() {
      const markIntent = () => { lastIntent = performance.now(); };
      ['wheel', 'touchstart', 'touchmove', 'pointerdown'].forEach((type) => {
        window.addEventListener(type, markIntent, { passive: true });
      });
      window.addEventListener('keydown', (event) => { if (SCROLL_KEYS.has(event.key)) markIntent(); });
      window.addEventListener('scroll', () => {
        if (userScrolled || pill.hidden) return;
        // Programmatic scrolls (focus return, anchor jumps) just move the reference point.
        if (performance.now() - lastIntent > INTENT_MS) { armY = window.scrollY; return; }
        if (Math.abs(window.scrollY - armY) < SCROLL_ARM_PX) return;
        userScrolled = true;
        syncPill();
      }, { passive: true });

      // Buy panel, product forms, add-to-cart buttons. 24px margin + delayed reveal = no flicker at the edge;
      // hiding is immediate so the pill never covers a control.
      const setControls = (onScreen) => {
        clearTimeout(revealTimer);
        if (onScreen) {
          controlsOnScreen = true;
          syncPill();
        } else {
          revealTimer = setTimeout(() => { controlsOnScreen = false; syncPill(); }, 150);
        }
      };
      const onScreen = new Set();
      let stickyShown = false;
      const update = () => setControls(onScreen.size > 0 || stickyShown);

      const controls = [...document.querySelectorAll(
        '#choose-plan, .n50-pdp-buy, product-form-component, form[action*="/cart/add"], add-to-cart-component, button[name="add"]'
      )].filter((el) => !el.closest('[data-n50-bogo], cart-drawer-component, dialog'));
      if (controls.length && 'IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) onScreen.add(entry.target);
            else onScreen.delete(entry.target);
          });
          update();
        }, { rootMargin: '24px 0px' });
        controls.forEach((el) => io.observe(el));
      }

      // PDP sticky buy bar: fixed, so watch its visibility class rather than intersection.
      const sticky = document.querySelector('[data-n50-pdp-sticky]');
      if (sticky && 'MutationObserver' in window) {
        const check = () => {
          stickyShown = root.dataset.pillSticky !== 'false' && !sticky.hidden && sticky.classList.contains('is-visible')
            && getComputedStyle(sticky).display !== 'none';
          update();
          requestAnimationFrame(placePill);
        };
        new MutationObserver(check).observe(sticky, { attributes: true, attributeFilter: ['class', 'hidden'] });
        window.addEventListener('resize', check, { passive: true });
        check();
      }
    }

    function render() {
      const ms = remaining();
      const label = format(ms);
      times.forEach((el) => { el.textContent = label; });
      if (ms === 0 && startedAt() && !expired) expire();
    }

    function startClock() {
      if (!startedAt()) store.set('start', String(Date.now()));
      render();
      if (!ticker && !expired) ticker = setInterval(render, 1000);
    }

    function open(trigger) {
      if (dialog.open) return;
      if (testMode) root.hidden = false;
      if (root.hidden) return;
      stopTriggers();
      returnFocus = trigger || document.activeElement;
      startClock();
      if (root.hidden) return;
      if (pill) { pill.hidden = true; syncPill(); }
      dialog.showModal();
      const first = dialog.querySelector('[data-n50-bogo-close]');
      if (first) first.focus();
    }

    function close() {
      if (dialog.open) dialog.close();
    }

    dialog.addEventListener('close', () => {
      store.set('dismissed', '1');
      if (testMode || !expired || expiredMode === 'soft') showPill(true);
      const target = returnFocus && returnFocus !== document.body && document.contains(returnFocus) && isVisible(returnFocus)
        ? returnFocus
        : null;
      if (target) target.focus();
      returnFocus = null;
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    });

    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = [...dialog.querySelectorAll(FOCUSABLE)].filter(isVisible);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    root.querySelectorAll('[data-n50-bogo-close], [data-n50-bogo-close-link]').forEach((el) => {
      el.addEventListener('click', close);
    });

    const errorEl = root.querySelector('[data-n50-bogo-error]');
    const claimButtons = [...root.querySelectorAll('[data-n50-bogo-claim]')];

    function markClaimed() {
      store.set('claimed', '1');
      if (pill && !pill.hidden) showPill();
    }

    function setBusy(button, busy) {
      claimButtons.forEach((el) => {
        if (el.tagName === 'BUTTON') el.disabled = busy;
      });
      // Desktop and mobile labels are separate spans; only one is displayed at a time.
      const texts = button.querySelectorAll('.n50-bogo__btn-text');
      if (busy) {
        button.setAttribute('aria-busy', 'true');
        if (root.dataset.loadingLabel) {
          texts.forEach((text) => {
            text.dataset.label = text.textContent;
            text.textContent = root.dataset.loadingLabel;
          });
        }
      } else {
        button.removeAttribute('aria-busy');
        texts.forEach((text) => {
          if (text.dataset.label) text.textContent = text.dataset.label;
        });
      }
    }

    async function postJSON(url, body) {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.description || data.message || `${url} ${res.status}`);
      return data;
    }

    const codeApplied = (cart, code) =>
      (cart?.discount_codes || []).some((d) => d.code?.toLowerCase() === code.toLowerCase());

    function openCart(cart, sections) {
      const drawer = document.querySelector('cart-drawer-component');
      if (!drawer) {
        window.location.href = root.dataset.fallbackUrl || '/products/10-in-1-health-system?open_cart=1';
        return;
      }
      if (dialog.open) dialog.close();
      // Same event shape Horizon's product form uses: cart-items re-renders, cart-icon updates, drawer auto-opens.
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: root.id,
            data: { source: 'n50-bogo-popup', itemCount: cart.item_count, sections: sections || undefined },
          },
        })
      );
      requestAnimationFrame(() => openDrawer(drawer));
    }

    function openDrawer(drawer = document.querySelector('cart-drawer-component')) {
      if (!drawer) {
        window.location.href = root.dataset.fallbackUrl || '/products/10-in-1-health-system?open_cart=1';
        return;
      }
      if (drawer.refs?.dialog?.open) return;
      if (typeof drawer.open === 'function') drawer.open();
      else if (typeof drawer.showDialog === 'function') drawer.showDialog();
    }

    async function claim(button) {
      let items;
      try { items = JSON.parse(button.dataset.items || '[]'); } catch { items = []; }
      if (!items.length || button.getAttribute('aria-busy') === 'true') return;

      const code = (root.dataset.code || '').trim();
      if (errorEl) errorEl.hidden = true;
      setBusy(button, true);

      try {
        await postJSON('/cart/add.js', { items });

        let sections = null;
        const variantId = String(items[0]?.id || '');
        if (code) {
          const sectionId = document.querySelector('cart-items-component[data-section-id]')?.dataset.sectionId;
          // _bogo_active switches on the cart's two-way gift sync (component-cart-items.js) on every page.
          const attributes = { _bogo_active: code, _bogo_variant: variantId, _bogo_notice: '' };
          const body = { discount: code, attributes };
          if (sectionId) {
            body.sections = sectionId;
            body.sections_url = window.location.pathname;
          }
          const updated = await postJSON('/cart/update.js', body).catch(() => null);
          sections = updated?.sections || null;
          if (!codeApplied(updated, code)) {
            await fetch(`/discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' }).catch(() => {});
            if (!updated) await postJSON('/cart/update.js', { attributes }).catch(() => {});
            sections = null;
          }
        }

        const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        if (code) {
          try {
            localStorage.setItem('next50_bogo_active', JSON.stringify({ code, variant: variantId, token: cart.token }));
          } catch { /* storage blocked */ }
        }
        markClaimed();
        setBusy(button, false);
        openCart(cart, sections);
      } catch (error) {
        setBusy(button, false);
        if (errorEl) errorEl.hidden = false;
        console.warn('[next50-bogo-popup] claim failed', error);
      }
    }

    claimButtons.forEach((el) => {
      if (el.tagName === 'A') {
        el.addEventListener('click', markClaimed);
      } else {
        el.addEventListener('click', () => claim(el));
      }
    });

    // The cart owns the offer state: follow it when the shopper removes or re-adds the free tubs in the drawer.
    document.addEventListener('next50:bogo-state', (event) => {
      const active = Boolean(event.detail?.active);
      if (active === claimed()) return;
      store.set('claimed', active ? '1' : '');
      if (pill && !pill.hidden) showPill();
    });

    if (pill) {
      pill.setAttribute('aria-hidden', 'true');
      pill.inert = true;
      pill.tabIndex = -1;
      if (designMode) userScrolled = true;
      else watchPillGates();
      pill.addEventListener('click', () => {
        if (claimed()) openDrawer();
        else open(pill);
      });
      let queued = false;
      const queuePlace = () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => { queued = false; placePill(); });
      };
      window.addEventListener('resize', queuePlace);
      window.addEventListener('scroll', queuePlace, { passive: true });
      // The preview bar is injected late and can be hidden/shown by the merchant.
      setInterval(placePill, 1500);
    }

    root.n50Bogo = {
      reopen() {
        testMode = true;
        store.clear();
        expired = false;
        clearInterval(ticker);
        ticker = null;
        setState('active');
        root.classList.remove('n50-bogo--soft-expired');
        root.querySelectorAll('.n50-bogo__mtimer').forEach((el) => {
          el.hidden = el.hasAttribute('data-n50-bogo-soft');
        });
        close();
        open();
      },
    };

    if (designMode) {
      const sectionId = root.id.replace('n50-bogo-', '');
      document.addEventListener('shopify:section:select', (e) => { if (e.detail.sectionId === sectionId) open(); });
      document.addEventListener('shopify:section:deselect', (e) => { if (e.detail.sectionId === sectionId) close(); });
      return;
    }

    if (testMode) {
      open();
      return;
    }

    // Claimed, arrived via a cart link, or already closed it: pill only, no auto-open.
    if (pillOnly || (store.get('dismissed') && startedAt())) {
      if (!startedAt()) return;
      startClock();
      if (!expired || expiredMode === 'soft') showPill(true);
      return;
    }

    // Don't stack on top of the cart drawer / plan sheet; retry shortly instead.
    const autoOpen = () => {
      if (document.querySelector('dialog[open]')) {
        const retry = setTimeout(autoOpen, 2000);
        cleanups.push(() => clearTimeout(retry));
        return;
      }
      open();
    };

    if (delay >= 0) {
      const timer = setTimeout(autoOpen, delay * 1000);
      cleanups.push(() => clearTimeout(timer));
    }

    // Opens at N% of the scrollable page; whichever of delay / scroll / exit intent fires first wins.
    const scrollPct = Number(root.dataset.scroll) || 0;
    if (scrollPct > 0) {
      let fired = false;
      const onScroll = () => {
        if (fired) return;
        const scrollable = document.documentElement.scrollHeight - window.innerHeight;
        if (scrollable <= 0 || (window.scrollY / scrollable) * 100 < scrollPct) return;
        fired = true;
        autoOpen();
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      cleanups.push(() => window.removeEventListener('scroll', onScroll));
    }

    if (root.dataset.exitIntent === 'true' && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      const onOut = (event) => {
        if (!event.relatedTarget && event.clientY <= 0) autoOpen();
      };
      document.addEventListener('mouseout', onOut);
      cleanups.push(() => document.removeEventListener('mouseout', onOut));
    }
  }

  const boot = (scope = document) => scope.querySelectorAll('[data-n50-bogo]').forEach((root) => init(root));
  boot();

  // Console helper for QA: clears this campaign's saved state and opens the pop-up, ignoring suppression.
  window.next50BogoOpen = () => {
    document.querySelectorAll('[data-n50-bogo]').forEach((root) => {
      if (root.n50Bogo) {
        root.n50Bogo.reopen();
      } else {
        delete root.dataset.ready;
        init(root, true);
      }
    });
  };
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
